import { GoogleOAuthProvider } from "@react-oauth/google"
import {
  App as AntdApp,
  ConfigProvider,
  Layout,
  Spin,
  notification
} from "antd"
import en from "antd/es/date-picker/locale/en_US"
import enUS from "antd/es/locale/en_US"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import React, { useEffect, useMemo } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useResizeDetector } from "react-resize-detector"
import { useNavigate } from "react-router"
import { Outlet, useLocation } from "react-router-dom"

import { CurlIndicator } from "../../components/CurlRepeater.tsx"
import { MediaIndicator } from "../../components/MediaIndicator"
import Spinner from "../../components/Spinner"
import { BREAKPOINTS, antBreakpoints } from "../../constants/breakpoint"
import { ROLE_IDS } from "../../constants/roles.ts"
import { configNotification } from "../../constants/notification.config"
import { DateFormatContext } from "../../contexts/date-format.ts"
import withAuth from "../../hoc/WithAuth.tsx"
import { useSessionActivityPing } from "../../hooks/use-session-activity-ping.ts"
import { useStartup } from "../../hooks/use-startup.ts"
import useWindowSize from "../../hooks/use-window-size"
import i18n from "../../i18n"
import { errorReset } from "../../redux/errors.slice"
import { navSetProp } from "../../redux/nav/nav.slice.ts"
import { type RootState } from "../../store.ts"
import { defaultTheme } from "../../theme"
import { type HandledErrorAction } from "../../errors/handled-error"
import { showErrorNotifications } from "../../utils/handlers/handle-error"
import { DATE_FORMAT } from "../../utils/date/date.ts"
import {
  logout as logoutThunk,
  verifyToken as verifyTokenThunk
} from "../LoginPage/redux/auth.thunks.ts"
import { isProjectEducationPath } from "../ProjectEducationPage/project-education-path.ts"
import { isSharedFilePath } from "../SharedFilePage/shared-file-path.ts"
import { loadUIPreferences as loadUIPreferencesThunk } from "../User/redux/ui-preferences.thunks.ts"
import { SupportButton } from "../SupportButton.tsx"
import { DATE_FORMATS_OBJ } from "../TeamManagementPage/constants/date-formats.ts"
import { useCallbackRef } from "../../hooks/use-callback-ref.ts"

dayjs.extend(utc)

function RootLayout({
  auth,
  setNavProp,
  userInfo,
  isDesktop,
  isMobile,
  error,
  verifyToken,
  errorReset,
  loadUIPreferences,
  logout
}: ConnectedProps<typeof connector>) {
  const { width, ref } = useResizeDetector()
  const [, innerHeight] = useWindowSize()
  const [api, contextHolder] = notification.useNotification()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  // Checking a stored session here signs an expired share-link visitor out to /login.
  const sessionToken =
    isSharedFilePath(pathname) || isProjectEducationPath(pathname)
      ? null
      : auth?.accessToken
  const usesSession = !!sessionToken
  const dateFormat =
    (userInfo?.date_format
      ? DATE_FORMATS_OBJ[userInfo.date_format]?.label
      : null) ?? DATE_FORMAT

  const globalBuddhistLocale: typeof enUS = useMemo(() => {
    return {
      ...enUS,
      DatePicker: {
        ...enUS.DatePicker!,
        lang: {
          ...en.lang,

          fieldDateTimeFormat: dateFormat
            ? `${dateFormat} HH:mm:ss`
            : enUS.DatePicker?.lang?.fieldDateTimeFormat,
          fieldDateFormat: dateFormat || enUS.DatePicker?.lang?.fieldDateFormat,
          placeholder: dateFormat || enUS.DatePicker?.lang?.placeholder
        }
      }
    } as typeof enUS
  }, [dateFormat])

  // STARTUP
  useStartup(auth)

  useSessionActivityPing(usesSession)

  useEffect(() => {
    if (innerHeight)
      document.documentElement.style.setProperty(
        "--app-height",
        `${innerHeight}px`
      )
  }, [innerHeight])

  // REFRESH TOKEN
  const verifySession = useCallbackRef((token: string) =>
    verifyToken({ token, navigate })
  )
  useEffect(() => {
    if (sessionToken) verifySession(sessionToken)
  }, [sessionToken, auth?.refreshToken, verifySession])

  // UI PREFERENCES
  // Keyed on the boolean, not the token: the token rotates on every refresh, and
  // a reload landing mid-save would overwrite the choice the user just made.
  const loadUIPreferencesLatest = useCallbackRef(loadUIPreferences)
  useEffect(() => {
    if (usesSession) void loadUIPreferencesLatest()
  }, [usesSession, loadUIPreferencesLatest])

  // ERRORS
  const runErrorAction = ({ kind, href }: HandledErrorAction) => {
    if (kind === "restart-login") {
      logout({ navigate })
      return
    }
    window.location.assign(href)
  }

  const reportError = useCallbackRef(() => {
    if (error) {
      showErrorNotifications(error, api, runErrorAction)
      errorReset()
    }
  })
  const hasError = !!error
  useEffect(() => {
    if (hasError) reportError()
  }, [hasError, reportError])

  // RESIZE
  const setNavPropLatest = useCallbackRef(setNavProp)
  const hasUserInfo = !!userInfo
  useEffect(() => {
    const correctWidth = width || window.innerWidth

    if (correctWidth > BREAKPOINTS.MD && !isDesktop) {
      setNavPropLatest("isDesktop", true)
      setNavPropLatest("isMobile", false)
    }

    if (correctWidth <= BREAKPOINTS.MD && !isMobile) {
      setNavPropLatest("isDesktop", false)
      setNavPropLatest("isMobile", true)
    }
  }, [width, hasUserInfo, isDesktop, isMobile, setNavPropLatest])

  // SET LANGUAGE
  useEffect(() => {
    i18n.changeLanguage(userInfo?.language || "en")
    dayjs.locale(
      userInfo?.language === "zh-hans" ? "zh-cn" : userInfo?.language || "en"
    )
  }, [userInfo?.language])

  Spin.setDefaultIndicator(<Spinner />)

  // The dev-only breakpoint/curl overlays already no-op in production builds
  // (IS_PROD), but a customer viewing the portal in a dev/staging build
  // should never see internal tooling either — the portal is customer-facing
  // regardless of deploy stage.
  const isCustomer = userInfo?.role?.id === ROLE_IDS.CUSTOMER

  return (
    <DateFormatContext.Provider value={dateFormat}>
      <ConfigProvider
        locale={globalBuddhistLocale}
        theme={{
          cssVar: true,
          ...defaultTheme,
          token: {
            ...defaultTheme?.token,
            ...antBreakpoints
          }
        }}
      >
        {contextHolder}
        <GoogleOAuthProvider
          clientId={import.meta.env.VITE_APP_GOOGLE_CLIENT_ID}
        >
          <AntdApp notification={configNotification}>
            <Layout id="main-layout" ref={ref} className="main-layout">
              {!isCustomer && <MediaIndicator />}
              {!isCustomer && <CurlIndicator />}
              <SupportButton />
              <Outlet />
            </Layout>
          </AntdApp>
        </GoogleOAuthProvider>
      </ConfigProvider>
    </DateFormatContext.Provider>
  )
}

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
  userInfo: state.auth.userInfo,
  isDesktop: state.nav.isDesktop,
  isMobile: state.nav.isMobile,
  error: state.errors.data
})

const mapDispatchToProps = {
  setNavProp: navSetProp,
  errorReset: errorReset,
  verifyToken: verifyTokenThunk,
  loadUIPreferences: loadUIPreferencesThunk,
  logout: logoutThunk
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default withAuth(connector(RootLayout))
